import { queryOptions } from '@tanstack/react-query';
import { api, apiList } from '@/lib/api-client';
import type { Client, ClientDetail, EntityGroup, EntityValue, TicketListItem } from '@erp/shared';

export const CLIENTS_PAGE_SIZE = 50;

export function clientsListOptions(params: { q: string; page: number }) {
  return queryOptions({
    queryKey: ['erp', 'clients', 'list', params] as const,
    queryFn: async () => {
      const res = await apiList<Client>('/clients', {
        query: {
          search: params.q || undefined,
          limit: CLIENTS_PAGE_SIZE,
          offset: (params.page - 1) * CLIENTS_PAGE_SIZE,
        },
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function clientDetailOptions(clientId: number) {
  return queryOptions({
    queryKey: ['erp', 'clients', 'detail', clientId] as const,
    queryFn: async () => {
      const res = await api<ClientDetail>(`/clients/${clientId}`);
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function clientEntitiesOptions(clientId: number) {
  return queryOptions({
    queryKey: ['erp', 'clients', 'entities', clientId] as const,
    queryFn: async () => {
      const res = await api<EntityValue[]>(`/clients/${clientId}/entities`);
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/** Definições de grupos/campos (para opções de single_select) aplicados a clientes. */
export function clientEntityGroupsOptions() {
  return queryOptions({
    queryKey: ['erp', 'entities', 'groups', 'client'] as const,
    queryFn: async () => {
      const res = await api<EntityGroup[]>('/entities', { query: { applied_in: 'client' } });
      if (res.error) throw res.error;
      return res.data.filter((g) => g.appliedIn === 'client');
    },
    staleTime: 60_000,
  });
}

export function clientTicketsOptions(clientId: number) {
  return queryOptions({
    queryKey: ['erp', 'tickets', 'by-client', clientId] as const,
    queryFn: async () => {
      const res = await apiList<TicketListItem>('/tickets', {
        query: { client_ids: [clientId], status: 'all', limit: 20 },
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function errorMessage(err: unknown): string {
  if (
    err !== null &&
    typeof err === 'object' &&
    'error' in err &&
    typeof (err as { error: unknown }).error === 'string'
  ) {
    return (err as { error: string }).error;
  }
  return err instanceof Error ? err.message : 'Erro inesperado';
}
